import { typeScale } from '@/theme/tokens';
import { Text } from '@/ui/primitives';
import { formatTextValue } from '../formatters/infoFieldUtils';
import { Stack } from '../layout/Stack';
import type { SpacingToken } from '@/theme/tokens';
import type { SxProps, Theme } from '@/ui/primitives';
import type { ReactElement, ReactNode } from 'react';

type InfoFieldProps = {
  label: string;
  value?: ReactNode;
  align?: 'left' | 'right';
  gap?: SpacingToken;
  labelSx?: SxProps<Theme>;
  labelTextRole?: 'label' | 'body';
};

const resolveDisplayValue = (value: ReactNode | undefined): ReactNode => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string') return formatTextValue(value);
  if (typeof value === 'number') return String(value);
  return value;
};

const infoFieldLabelSx: SxProps<Theme> = {
  fontSize: `${typeScale.md}px`,
  letterSpacing: 0,
  lineHeight: 1.45,
  textTransform: 'uppercase',
};

const infoFieldValueSx: SxProps<Theme> = {
  fontSize: `${typeScale.md}px`,
  lineHeight: 1.45,
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  '& .MuiLink-root, & .MuiTypography-root': {
    fontSize: 'inherit',
    lineHeight: 'inherit',
  },
};

export const InfoField = ({
  label,
  value,
  align = 'left',
  gap = 'md',
  labelSx,
  labelTextRole = 'label',
}: InfoFieldProps): ReactElement => {
  const displayValue = resolveDisplayValue(value);
  return (
    <Stack gap={gap} className="flowqa-info-field">
      <Text
        role={labelTextRole}
        color="textSecondary"
        align={align}
        className="flowqa-info-field-label"
        sx={
          labelSx
            ? [infoFieldLabelSx, ...(Array.isArray(labelSx) ? labelSx : [labelSx])]
            : infoFieldLabelSx
        }
      >
        {label}
      </Text>
      <Text
        role="body"
        align={align}
        component="div"
        className="flowqa-info-field-value"
        sx={infoFieldValueSx}
      >
        {displayValue}
      </Text>
    </Stack>
  );
};
